import { useCreateSelfAssemblyBumbalDate } from "app/hooks/checkout/useCreateSelfAssemblyBumbalDate";
import { useOnPurchase } from "app/hooks/shared/analytics/useOnPurchase";
import StatusGif from "entities/status-gif";
import StatusPaymentInfo from "entities/status-payment-info";
import StatusActionButtons from "features/status-action-buttons";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { OrderPayment } from "shared/api/middleware-api-client";
import Confetti from "shared/ui/confetti";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import { isDevelopmentEnvironment } from "shared/utils/checkEnvironment";
import { idealId, sprayPayId } from "shared/utils/payment";
import { twMerge } from "tailwind-merge";
import StatusSupport from "widgets/status-support";
import { useCartContext } from "../../../../../app/providers/CartProvider";
import { OrderType } from "../../../../payment/providers/PaymentProvider";
import { ErrorComponent } from "./ErrorComponent";
import { useCreateStandardOrderBumbalDate } from "app/hooks/checkout/useCreateStandardOrderBumbalDate";
import { useSearchParams } from "react-router-dom";

interface ConfirmationProps {
  payment: OrderPayment | undefined;
  orderNumber: string;
  type: string;
  order: any;
  isSelfAssembly: boolean;
}

const Confirmation = ({
  payment,
  orderNumber,
  type,
  order,
  isSelfAssembly,
}: ConfirmationProps) => {
  const { setCartToDefault, getCart } = useCartContext();

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const { sendOnDoorPurchaseAnalytics, sendOnStandardOrderPurchaseAnalytics } =
    useOnPurchase();
  const { makeBumbalDateSelfAssembly, isBumbalDateError } =
    useCreateSelfAssemblyBumbalDate();

  const [searchParams] = useSearchParams();
  // Panels bumbal date creation
  const createBumbalDateMutation = useCreateStandardOrderBumbalDate();

  const panelOrderID = searchParams.get("reference");

  const cart = getCart();

  useEffect(() => {
    async function createBumbalDateSelfAssembly() {
      await makeBumbalDateSelfAssembly();
    }
    if (!payment) return;

    if (payment.status === "paid") {
      if (isSelfAssembly) {
        createBumbalDateSelfAssembly();
      } else if (
        type === OrderType.STANDARD_PRODUCT &&
        panelOrderID &&
        cart.delivery.date
      ) {
        createBumbalDateMutation.mutateAsync({
          orderId: panelOrderID,
          type: cart.bumbalFlow || "",
          selectedDate: new Date(cart.delivery.date),
        });
      }

      // For orders with a payment, by current logic the purchased GA4 events are set at the confirmation page
      if (type === OrderType.STANDARD_PRODUCT) {
        if (panelOrderID)
          sendOnStandardOrderPurchaseAnalytics(
            { id: panelOrderID, orderNumber: orderNumber },
            cart,
          );
      } else {
        sendOnDoorPurchaseAnalytics(order);
      }

      localStorage.clear();
      setCartToDefault();
    }

    setPageContent();
  }, [payment]);

  const setPageContent = () => {
    if (type === OrderType.STANDARD_PRODUCT) {
      setStandardProductPageContent();
    } else if (type === OrderType.DOOR) {
      if (order.shoppingCart.sampleSale) {
        setSampleSalePageContent();
      } else {
        setDoorPageContent();
      }
    } else {
      setDefaultPageContent();
    }
  };

  const setSampleSalePageContent = () => {
    setTitle("Je betaling is gelukt!");
    setDescription(
      `Je krijgt een bevestiging per mail en wij gaan je bestelling in orde maken voor verzending.

Twee dagen voor levering ontvang je van ons rond de klok van 18.00 uur (ook in het weekend) een tijdsindicatie waarin je de chauffeur kunt verwachten.

Tot snel!`,
    );
  };

  const setDoorPageContent = () => {
    if (!payment) return;

    switch (payment.transaction?.method) {
      case idealId:
        setTitle("Je betaling is gelukt!");
        if (isSelfAssembly) {
          setDescription(
            `Nu de betaling voor ${orderNumber} bij ons binnen is, kunnen wij aan de slag met het produceren van je bestelling. Binnen enkele minuten ontvang je een mail ter bevestiging van je bestelling. Hierin kun je ook de bezorgdatum en je bestelnummer terugvinden.`,
          );
        } else {
          setDescription(
            `Nu de betaling van ${orderNumber} bij ons binnen is, kun jij overgaan tot het plannen van je 
            montage-afspraak. Dit is nodig want zolang wij niet weten wanneer we welkom zijn om je bestelling te 
            leveren, blijft de productie nog on-hold staan. Plan hier direct een datum voor levering en montage.`,
          );
        }
        break;
      case sprayPayId:
        setTitle("Bedankt voor je betaling met SprayPay");
        if (isSelfAssembly) {
          setDescription("TODOTEXT");
        } else {
          setDescription(
            `Jouw aanvraag voor betalen in termijnen is goedgekeurd door SprayPay. We gaan starten met de
            productie van jouw order.`,
          );
        }
        break;
      default:
        setDefaultPageContent();
    }
  };

  const setStandardProductPageContent = () => {
    setTitle("Je betaling is gelukt!");
    setDescription(
      `Nu de betaling voor ${orderNumber} bij ons binnen is, kunnen wij aan de slag met het produceren van je bestelling. Binnen enkele minuten ontvang je een mail ter bevestiging van je bestelling. Hierin kun je ook de bezorgdatum en je bestelnummer terugvinden.`,
    );
  };

  const setDefaultPageContent = () => {
    setTitle("Je betaling is gelukt!");
    setDescription(
      `Nu de betaling van ${orderNumber} bij ons binnen is, kun jij overgaan tot het plannen van je 
       montage-afspraak. Dit is nodig want zolang wij niet weten wanneer we welkom zijn om je bestelling te 
       leveren, blijft de productie nog on-hold staan. Plan hier direct een datum voor levering en montage.`,
    );
  };

  const showOrderTotal = useMemo(() => {
    if (payment && payment.transaction) {
      if (payment.transaction.method === sprayPayId) return false;
      if (payment.orderType === "StandardOrder") return false;
      if (order.paymentData.paid === order.paymentData.total) return false;
    }

    return true;
  }, [payment, order]);

  return (
    <div
      className={twMerge(
        isDevelopmentEnvironment() ? "gg-mx-auto" : "",
        "gg-max-w-3xl",
      )}
    >
      <Confetti />

      <AnimatePresence>
        {isBumbalDateError ? (
          <ErrorComponent />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <div className="gg-mb-12">
              <StatusGif
                src={`https://media1.giphy.com/media/jGgKShPYV6HQDYgdCq/giphy.gif`}
                alt="Een GewoonGers Gif. Marketeer geeft een duimpje."
              />

              <Heading headingLevel="h1" className="gg-mb-6 md:!gg-text-5xl">
                {title}
              </Heading>

              <Heading headingLevel="h2" className="gg-mb-5 gg-text-2xl">
                {order?.shoppingCart?.sampleSale
                  ? "Bedankt voor je bestelling, de betaling is bij ons binnen."
                  : "Bedankt voor je bestelling, je betaling was succesvol."}
              </Heading>

              <Text className="gg-text-lg">{description}</Text>
            </div>

            <div className="gg-mb-6">
              <StatusActionButtons className="" />
            </div>

            {!order?.shoppingCart?.sampleSale && (
              <>
                {showOrderTotal && !!payment && (
                  <StatusPaymentInfo className="gg-mb-12" />
                )}
              </>
            )}
            <StatusSupport />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default Confirmation;
